import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getUser, getIntercomToken } from "~/api/account/requests";
import { AccountContext } from "~/contexts/Account";
import { IAccount } from "~/types/account";
import { Userpilot } from "userpilot";
import _debounce from "lodash/debounce";
import { useIntercom } from "react-use-intercom";
import { intercomKey } from "~/helpers/environment";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { useStore } from "~/hooks/useStore";
import { logger } from "~/helpers/logger";

// Extend the Window interface to include $crisp
declare global {
  interface Window {
    $crisp: any[];
  }
}

export const useAccount = () => {
  const intercomBooted = useRef(false);
  const { account, setAccount } = useContext(AccountContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isInterCom, setIsInterCom] = useState(false);
  const { boot, show } = useIntercom();
  const { store } = useStore();

  const bootIntercomForAccount = async (account: IAccount) => {
    if (!intercomKey) {
      setIsInterCom(false);
      return;
    }

    let intercom_user_jwt: string;
    try {
      ({ intercom_user_jwt } = await getIntercomToken());
    } catch (error) {
      logger.error("Failed to fetch Intercom token", error);
      return;
    }

    setIsInterCom(true);
    boot({
      email: account.email,
      userId: account.id.toString(),
      name: account.name,
      intercomUserJwt: intercom_user_jwt,
      customAttributes: {
        app: "alidrop",
        shop_name: store?.name,
        shop_url: store?.url,
        shop_email: store?.email,
        shop_phone: store?.phone,
      },
    } as Parameters<typeof boot>[0]);
  };

  const updateCrispUser = (account: IAccount) => {
    if (typeof window !== "undefined" && window.$crisp) {
      window.$crisp.push(["set", "user:email", account.email]);
      window.$crisp.push(["set", "user:nickname", account.name]);

      const sessionData = [
        ["app", "alidrop"],
        ["user_id", "alidrop_" + account.id.toString()],
        ["shop_name", store?.name],
        ["shop_url", store?.url],
        ["shop_email", store?.email],
        ["shop_phone", store?.phone],
        ["payment_provider", account.payment_provider],
        ["signup_origin", account.signup_origin],
        ["plan_name", account.active_subscription?.plan?.name],
      ].filter(([, value]) => value !== undefined && value !== null);

      window.$crisp.push(["set", "session:data", sessionData]);
    }
  };

  const bootIntercom = () => (account ? bootIntercomForAccount(account) : null);

  const showIntercom = () => {
    if (account) {
      show();
      if (typeof window !== "undefined" && window.$crisp) {
        window.$crisp.push(["do", "chat:open"]);
      }
    }
  };

  const setAccountWithThirdParties = (acc: IAccount | null) => {
    setAccount(acc);

    if (acc) {
      // Set user context for logging/analytics
      logger.setUser({
        id: acc.id,
        email: acc.email,
        username: acc.name,
      });

      // Add additional context for logging/analytics
      logger.setContext("account", {
        payment_provider: acc.payment_provider,
        signup_origin: acc.signup_origin,
        created_at: acc.created_at,
        subscription_active: !!acc.active_subscription,
        plan_name: acc.active_subscription?.plan?.name,
      });

      Userpilot.identify(acc.id.toString(), {
        email: acc.email,
        created_at: acc.created_at,
        name: acc.name,
        payment_provider: acc.payment_provider,
        signup_origin: acc.signup_origin,
      });

      // Update Crisp user info whenever account is available
      updateCrispUser(acc);

      // Boot Intercom only once when conditions are met
      if (
        (acc.active_subscription || DISABLE_PAYMENTS) &&
        !intercomBooted.current
      ) {
        intercomBooted.current = true;
        bootIntercomForAccount(acc);
      }
    } else {
      // Clear user context on logout
      logger.clearUser();
    }
  };

  const getAccount = async () => {
    setIsLoading(true);
    try {
      const profile = (await getUser()) as IAccount;
      setAccountWithThirdParties(profile);
      setIsLoading(false);
      return profile;
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };
  // Debounce prevents duplicate calls when multiple components mount at once
  // (e.g. Sidebar + PageWrapper). Reduced to 50 ms from 200 ms to speed up
  // fresh-page-load account resolution without losing deduplication.
  // The catch() ensures that API failures during silent background fetches
  // don't create unhandled promise rejections.
  const debouncedGetAccount = useCallback(
    _debounce(() => getAccount().catch((error) => logger.error("Failed to fetch account", error)), 50),
    []
  );

  useEffect(() => {
    if (!account) {
      debouncedGetAccount();
      setIsLoading(false);
    }
  }, []);

  const isPerpetual = () => {
    if (!account) return false;
    return account.last_subscription?.plan?.name === "Perpetual Unicorn Plan";
  };

  return {
    account,
    isInterCom,
    setAccount: setAccountWithThirdParties,
    getAccount,
    refetch: getAccount,
    isPerpetual: isPerpetual(),
    bootIntercom,
    isLoading,
    showIntercom,
  };
};
